/**
 * Cover → defence math for browser attack rolls.
 *
 * Used by ability/rolls/attack-roll.ts and ability/templates/template-weapon-attack-roll.ts.
 * Spatial cover classification lives in ability/cover/directional-cover.ts.
 *
 * Legacy symbol correspondence:
 *   ft_spatial_target_defense_snapshot()    → coverDefenseSnapshot()
 *   ft_attacker_ignores_cover_defense()     → attackerIgnoresCoverDefense()
 *   FT_DIRECTIONAL_COVER_*_DEFENSE_DELTA    → PARTIAL_DEFENSE_DELTA / HEAVY_DEFENSE_DELTA
 *
 * ⛔ The one rule that must survive the port: ignoring cover suppresses only the ENVIRONMENTAL AC
 * that partial/heavy cover grants. A `blocked` tier means no line of sight, which is a TARGETING
 * gate, not a defence bonus — it is never ignorable, whatever the attacker carries.
 */

export const PARTIAL_DEFENSE_DELTA = 2;
export const HEAVY_DEFENSE_DELTA = 4;

export type CoverTier = 'none' | 'partial' | 'heavy' | 'blocked';

/** Roll-type UUIDs, matching RollType::getId() on the PHP side. */
export const WEAPON_RANGED_ATTACK_ROLL = '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';
export const SPELL_RANGED_ATTACK_ROLL = 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C';

/** A creature/item/attack-context flag bag, already flattened to booleans by the caller. */
export interface CoverIgnoreFlags {
    rangedWeaponAttacksIgnoreCover?: boolean;
    rangedSpellAttacksIgnoreCover?: boolean;
    rangedAttacksIgnoreCover?: boolean;
    /** Partial tier only, weapon or spell (2026-09-14, ranged_attacks_ignore_partial_cover). */
    rangedAttacksIgnorePartialCover?: boolean;
}

export interface CoverDefenseInput {
    tier: CoverTier;
    /** The delta the geometry pass measured, BEFORE any ignore is applied. */
    effectiveDefenseDelta: number;
    baseEc: number;
    /** Stored AC with any legacy cover-status environmental AC already removed. */
    baseAc: number;
    /** The roll type actually being made. Anything but a ranged attack roll ignores nothing. */
    rollTypeId: string | null;
    /** Attacker root flags, then the item's, then its nested attack context — first true wins. */
    sources: readonly CoverIgnoreFlags[];
}

export interface CoverDefenseSnapshot {
    baseEc: number;
    baseAc: number;
    environmentalAcDelta: number;
    effectiveAc: number;
    coverDefenseIgnored: boolean;
}

/**
 * Mirror of ft_attacker_ignores_cover_defense(). The generic flag covers both barrels; the specific
 * ones are gated on which barrel fired. A non-ranged-attack roll exempts nothing, not even via the
 * generic flag — the authored text is "against your ranged attacks".
 */
export function attackerIgnoresCoverDefense(
    rollTypeId: string | null,
    sources: readonly CoverIgnoreFlags[],
    /** Mirror of PHP's `$partial_only`: ask only about ranged_attacks_ignore_partial_cover. */
    partialOnly = false,
): boolean {
    // Strict, un-normalized compare — PHP does `$roll_type === RollType::getId(...)`, so folding
    // case here would make the mirror MORE permissive than the engine it claims parity with
    // (Codex review 2026-09-08). If case variance ever turns out to be real, it is a PHP-side bug
    // in ft_spatial_action_policy() too, and both sides get fixed together.
    const rt = rollTypeId ?? '';
    let specific: keyof CoverIgnoreFlags;
    if (rt === WEAPON_RANGED_ATTACK_ROLL) {
        specific = 'rangedWeaponAttacksIgnoreCover';
    } else if (rt === SPELL_RANGED_ATTACK_ROLL) {
        specific = 'rangedSpellAttacksIgnoreCover';
    } else {
        return false;
    }
    for (const source of sources) {
        if (!source) continue;
        if (partialOnly ? source.rangedAttacksIgnorePartialCover : (source.rangedAttacksIgnoreCover || source[specific])) return true;
    }
    return false;
}

/**
 * Mirror of ft_spatial_target_defense_snapshot() plus the ignore applied in
 * ft_spatial_action_geometry_context(). EC stays the hit/miss threshold; environmental cover only
 * raises the threshold for a FULL-AC hit.
 */
export function coverDefenseSnapshot(input: CoverDefenseInput): CoverDefenseSnapshot {
    const baseEc = Math.max(0, input.baseEc);
    const baseAc = Math.max(baseEc, input.baseAc);

    const measured = input.tier === 'partial' || input.tier === 'heavy'
        ? Math.max(0, input.effectiveDefenseDelta)
        : 0;

    // 'blocked' never reaches here as an ignorable tier: measured is already 0 for it, and the
    // targeting gate upstream refuses the action outright.
    const ignored = measured > 0
        && input.tier !== 'blocked'
        && (attackerIgnoresCoverDefense(input.rollTypeId, input.sources)
            || (input.tier === 'partial' && attackerIgnoresCoverDefense(input.rollTypeId, input.sources, true)));

    const environmentalAcDelta = ignored ? 0 : measured;

    return {
        baseEc,
        baseAc,
        environmentalAcDelta,
        effectiveAc: baseAc + environmentalAcDelta,
        coverDefenseIgnored: ignored,
    };
}
